import * as Q from "q";

import * as MentionAutocomplete from "Mention/Scripts/TFS.Mention.Autocomplete";
import * as MentionAutocompleteJQueryAutocomplete from "Mention/Scripts/TFS.Mention.Autocomplete.JQueryAutocomplete";
import * as Telemetry from "Mention/Scripts/TFS.Social.Telemetry";

import { WikiPage } from "TFS/Wiki/Contracts";

import { WikiPathsAutoCompleteProviderUtils, WikiPagePathsProvider } from "Wiki/Scripts/BaseWikiPathsAutoCompleteProviders";
import { getMdReadablePagePath } from "Wiki/Scripts/Helpers";
import { TemplateConstants } from "Wiki/Scripts/CommonConstants";

export class WikiIncludeAutoCompleteProvider extends MentionAutocompleteJQueryAutocomplete.JQueryAutocompletePlugin<{}, {}> {
    private _pagePathsProvider: WikiPagePathsProvider;

    constructor(
        options: MentionAutocomplete.IAutocompletePluginOptions,
        getPagesDelegate: () => IPromise<WikiPage[]>,
    ) {
        super(options);
        this._pagePathsProvider = new WikiPagePathsProvider(
            getPagesDelegate,
            getMdReadablePagePath,
            this._getTemplateDirectoryPrefix(),
            true,
            true);
    }

    public getPluginName(): string {
        return "WikiIncludeAutocompleteProvider";
    }

    public canOpen(inputText: MentionAutocomplete.IInputText): MentionAutocomplete.IRange {
        const pagePathStartIndex = WikiIncludeAutoCompleteProvider.getStartIndexOfIncludePath(inputText.textBeforeSelection);
        if (Boolean(pagePathStartIndex)) {
            return {
                start: pagePathStartIndex,
                end: inputText.textBeforeSelection.length,
            }
        }
        else {
            return null;
        }
    }

    // Provides suggestions on pattern match
    public getSuggestions(autocompleteText: MentionAutocomplete.IInputText): JQueryPromise<MentionAutocomplete.IResultWithTelemetry<MentionAutocomplete.ISearchResult<string>[], Telemetry.IAutocompleteSuggestEvent>> {
        const termMatches = WikiIncludeAutoCompleteProvider._getIncludePagePattern().exec(autocompleteText.textBeforeSelection);
        const filterPathsPromise = this._pagePathsProvider.search(termMatches[2]);

        const deferred = $.Deferred<MentionAutocomplete.IResultWithTelemetry<MentionAutocomplete.ISearchResult<string>[], Telemetry.IAutocompleteSuggestEvent>>();
        filterPathsPromise.done((result: MentionAutocomplete.ISearchResult<string>[]) => {
            deferred.resolve({
                result: result,
                telemetry: {},
            });
        }).fail((error: any) => {
            deferred.reject(error);
        });

        return deferred;
    }

    // Used to render the selected suggestion
    public renderSuggestion(ul: JQuery, suggestion: MentionAutocomplete.ISearchResult<string>): JQuery {
        return WikiPathsAutoCompleteProviderUtils.renderSuggestion(ul, suggestion);
    }

    // Provides text to be replaced when an option is selected
    public getReplacementText(inputText: MentionAutocomplete.IInputText, suggestion: MentionAutocomplete.ISearchResult<string>, previewReplacement: boolean): MentionAutocomplete.IInputText {
        let textBeforeSelection = inputText.textBeforeSelection;
        if (!previewReplacement) {
            const pagePathStartIndex = WikiIncludeAutoCompleteProvider.getStartIndexOfIncludePath(inputText.textBeforeSelection);
            const unchangedPrefixText: string = inputText.textBeforeSelection.substring(0, pagePathStartIndex);
            const replacementText = getMdReadablePagePath(suggestion.original);
            textBeforeSelection = unchangedPrefixText + replacementText;
        }

        return {
            textBeforeSelection,
            textInSelection: "",
            textAfterSelection: inputText.textAfterSelection,
        };
    }

    private _getTemplateDirectoryPrefix(): string {
        let directoryPath: string = TemplateConstants.TemplateDirectory;
        if (!TemplateConstants.TemplateDirectory.endsWith("\\") || !TemplateConstants.TemplateDirectory.endsWith("/")) {
            directoryPath += "/";
        }

        return directoryPath;
    }

    public static getStartIndexOfIncludePath(text: string): number {
        const matches = WikiIncludeAutoCompleteProvider._getIncludePagePattern().exec(text);
        if (Boolean(matches)) {
            return text.length - matches[2].length;
        }

        return null;
    }

    // Pattern to match include page syntax
    private static _getIncludePagePattern(): RegExp {
        //return new RegExp(escapeRegExp(`(^|\\n)[\\s]{0,3}:::[\\s]*${TemplateConstants.Identifier}[\\s]+([^\\s]*)$`));
        return /(^|\n)[\s]{0,3}:::[\s]*template[\s]+([\S]*)$/;
    }
}
